import { Action } from 'redux';
import { requestAction, doAsync } from '@common/components/common.actions';
import { utilityService } from '@services/UtilityService';
import { StatesViewModel, OptionsViewModel, AppDispatcher } from '@models/common.models';
import { cviService } from '@services/CVIService';
import {
  PurchaseRequestPartsViewModel,
  PurchaseRequestViewModel,
  PartsItemAmounts,
  PurchaseRequestPartsTotals
} from '@models/PurchaseRequest';
import { AppState } from 'src';
import { actions } from 'react-redux-form';
import FormNames from '@constants/FormNames.enum';
import { getPurchaseRequest } from 'src/purchasing/purchasing.actions';
import { swoPurchasingService } from '@services/SwoPurchasingService';

export enum PurchasePartsActionType {
  SetPartsUSStates = 'SetPartsUSStates',
  SetPartsLoading = 'SetPartsLoading',
  SetPartsBrands = 'SetPartsBrands',
  SetPartsSuppliers = 'SetPartsSuppliers',
  SetPartsStatuses = 'SetPartsStatuses',
  SetPartsEditItem = 'SetPartsEditItem',
  SetPartsItemTotal = 'SetPartsItemTotal'
}

/***** Parts US States *****/
export interface SetPartsUSStatesAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsUSStates;
  readonly usStates: Array<StatesViewModel>;
}
export function setPartsUSStates(usStates: Array<StatesViewModel>): SetPartsUSStatesAction {
  return {
    type: PurchasePartsActionType.SetPartsUSStates,
    usStates: usStates
  };
}
export function getPartsUSStates() {
  return async (dispatch: AppDispatcher) => {
    return await dispatch(requestAction(() => utilityService.getStates(), setPartsUSStates));
  };
}

/***** Parts Loading *****/
export interface SetPartsLoadingAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsLoading;
  readonly isLoading: boolean;
}
function setPartsLoading(loading: boolean): SetPartsLoadingAction {
  return {
    type: PurchasePartsActionType.SetPartsLoading,
    isLoading: loading
  };
}
export function togglePartsLoading(isLoadingParts: boolean) {
  return async (dispatch: AppDispatcher) => {
    await dispatch(setPartsLoading(isLoadingParts));
  };
}
/***** Parts Brands *****/
export interface SetPartsBrandsAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsBrands;
  readonly partsBrands: Array<OptionsViewModel>;
}
function setPartsBrands(partsBrands: Array<OptionsViewModel>): SetPartsBrandsAction {
  return {
    type: PurchasePartsActionType.SetPartsBrands,
    partsBrands: partsBrands
  };
}
export function getPartsBrands(cviId: number) {
  return async (dispatch: AppDispatcher) => {
    await dispatch(requestAction(() => cviService.getBrands(cviId), setPartsBrands));
  };
}

/***** Parts Suppliers *****/
export interface SetPartsSuppliersAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsSuppliers;
  readonly partsSuppliers: Array<OptionsViewModel>;
}
function setPartsSuppliers(partsSuppliers: Array<OptionsViewModel>): SetPartsSuppliersAction {
  return {
    type: PurchasePartsActionType.SetPartsSuppliers,
    partsSuppliers: partsSuppliers
  };
}
export function getPartsSuppliers() {
  return async (dispatch: AppDispatcher) => {
    await dispatch(requestAction(() => swoPurchasingService.getSuppliers(false), setPartsSuppliers));
  };
}

/***** Parts Statuses *****/
export interface SetPartsStatusesAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsStatuses;
  readonly partsStatuses: Array<OptionsViewModel>;
}
function setPartsStatuses(partsStatuses: Array<OptionsViewModel>): SetPartsStatusesAction {
  return {
    type: PurchasePartsActionType.SetPartsStatuses,
    partsStatuses: partsStatuses
  };
}
export function getPartsStatuses() {
  return async (dispatch: AppDispatcher) => {
    await dispatch(requestAction(() => swoPurchasingService.getStatuses(), setPartsStatuses));
  };
}

/***** Parts Edit Item *****/
export interface SetPartsEditItemAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsEditItem;
  readonly part: PurchaseRequestPartsViewModel;
  readonly index: number;
}
export function setPartsEditItem(part: PurchaseRequestPartsViewModel, partIndex: number): SetPartsEditItemAction {
  return {
    type: PurchasePartsActionType.SetPartsEditItem,
    part: part,
    index: partIndex
  };
}

function setPartsItemForm(part: PurchaseRequestPartsViewModel, partIndex: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    await dispatch(doAsync(setPartsEditItem(part, partIndex))).then(async () => {
      await dispatch(actions.load(FormNames.PurchasePartsItemForm, part));
    });
  };
}

export function editPartsItem(part: PurchaseRequestPartsViewModel, partIndex: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    await dispatch(setPartsItemForm(part, partIndex));
  };
}

export function calculatePartsItemAmounts(part: PurchaseRequestPartsViewModel) {
  let partsAmounts = {
    quantity: Math.round((+part.quantity! || 1) * 100) / 100,
    partCost: Math.round((+part.partCost! || 0) * 100) / 100,
    restockFee: Math.round((+part.restockFee! || 0) * 100) / 100,
    deliveryFee: Math.round((+part.deliveryFee! || 0) * 100) / 100,
    installationFee: Math.round((+part.installationFee! || 0) * 100) / 100
  } as PartsItemAmounts;
  partsAmounts.quantityPartCost = partsAmounts.partCost * partsAmounts.quantity;
  partsAmounts.estTax = Math.round((partsAmounts.quantityPartCost * 0.095 || 0) * 100) / 100;
  partsAmounts.total =
    partsAmounts.quantityPartCost +
    partsAmounts.estTax +
    partsAmounts.restockFee +
    partsAmounts.deliveryFee +
    partsAmounts.installationFee;
  return partsAmounts;
}

export function calculatePartsItem(part: PurchaseRequestPartsViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const prevState = getState();
    const purchasingState = prevState.purchasing;
    const partIndex = purchasingState.purchaseParts.partIndex;
    var statePart = purchasingState.purchaseParts.part;
    var formPart = part;
    if (formPart && statePart) {
      let partsAmounts = calculatePartsItemAmounts(formPart);

      var newPart: PurchaseRequestPartsViewModel = {
        ...statePart,
        quantity: partsAmounts.quantity,
        partCost: partsAmounts.partCost,
        quantityPartCost: partsAmounts.quantityPartCost,
        estTax: partsAmounts.estTax,
        restockFee: partsAmounts.restockFee,
        deliveryFee: partsAmounts.deliveryFee,
        installationFee: partsAmounts.installationFee,
        total: partsAmounts.total
      };

      await dispatch(setPartsItemForm(newPart, partIndex));
    }
  };
}

/***** Parts Calculate Item *****/
export interface SetPartsItemTotalAction extends Action {
  readonly type: PurchasePartsActionType.SetPartsItemTotal;
  readonly quantityPartCost: number;
  readonly estTax: number;
  readonly total: number;
}
export function setPartsItemTotal(quantityPartCost: number, estTax: number, total: number): SetPartsItemTotalAction {
  return {
    type: PurchasePartsActionType.SetPartsItemTotal,
    quantityPartCost: quantityPartCost,
    estTax: estTax,
    total: total
  };
}

export function calculatePartsItemTotal(fieldName: string, fieldValue: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    const prevState = getState();
    var formPart = { ...prevState.purchasePartsItemForm } as PurchaseRequestPartsViewModel;
    if (formPart) {
      formPart[fieldName] = fieldValue;

      let partsAmounts = calculatePartsItemAmounts(formPart);

      await dispatch(setPartsItemTotal(partsAmounts.quantityPartCost, partsAmounts.estTax, partsAmounts.total));
    }
  };
}

/***** Parts Remove Item *****/
export function removePartsItem(partIndex: number) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    dispatch(togglePartsLoading(true));
    const prevState = getState();
    const purchasingState = prevState.purchasing;
    const purchaseRequest = purchasingState.purchaseRequest;
    if (purchaseRequest && purchaseRequest.purchaseRequestInfo) {
      const stateParts = purchaseRequest.purchaseRequestInfo.parts;
      if (stateParts) {
        var newArray = stateParts.slice();
        if (partIndex >= 0) {
          newArray.splice(partIndex, 1);

          await dispatch(calculatePurchaseRequestParts(newArray, purchaseRequest));
        }
      }
    }
  };
}

/***** Parts Submit Item *****/
export function submitPartsItem(part: PurchaseRequestPartsViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    dispatch(togglePartsLoading(true));
    const purchasingState = getState().purchasing;
    const purchaseRequest = purchasingState.purchaseRequest;
    const partIndex = purchasingState.purchaseParts.partIndex;
    if (purchaseRequest && purchaseRequest.purchaseRequestInfo) {
      const stateParts = purchaseRequest.purchaseRequestInfo.parts;
      if (stateParts) {
        var newArray = stateParts.slice();
        let partsAmounts = calculatePartsItemAmounts(part);
        let prPart = {
          ...part,
          quantity: +part.quantity,
          partCost: +part.partCost,
          quantityPartCost: partsAmounts.quantityPartCost,
          estTax: partsAmounts.estTax,
          restockFee: +part.restockFee,
          deliveryFee: +part.deliveryFee,
          installationFee: +part.installationFee,
          total: partsAmounts.total
        } as PurchaseRequestPartsViewModel;

        partIndex < 0 ? newArray.push(prPart) : newArray.splice(partIndex, 1, prPart);

        await dispatch(calculatePurchaseRequestParts(newArray, purchaseRequest));
      }
    }
  };
}

export function calculatePurchaseRequestPartsTotals(parts: PurchaseRequestPartsViewModel[], replacementCost: number) {
  let partsTotals = {
    partCostTotal: parts.map(x => +x.quantityPartCost! || 0).reduce((a, b) => a + b, 0),
    estTaxTotal: parts.map(x => +x.estTax! || 0).reduce((a, b) => a + b, 0),
    restockFeeTotal: parts.map(x => +x.restockFee! || 0).reduce((a, b) => a + b, 0),
    deliveryFeeTotal: parts.map(x => +x.deliveryFee! || 0).reduce((a, b) => a + b, 0),
    installationFeeTotal: parts.map(x => +x.installationFee! || 0).reduce((a, b) => a + b, 0),
    partsTotal: parts.map(x => +x.total! || 0).reduce((a, b) => a + b, 0)
  } as PurchaseRequestPartsTotals;
  partsTotals.savings = partsTotals.partsTotal - replacementCost;
  return partsTotals;
}

function calculatePurchaseRequestParts(
  parts: PurchaseRequestPartsViewModel[],
  purchaseRequest: PurchaseRequestViewModel
) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    let partsTotals = calculatePurchaseRequestPartsTotals(parts, purchaseRequest.purchaseRequestInfo.replacementCost);

    let prVM: PurchaseRequestViewModel = {
      ...purchaseRequest,
      purchaseRequestInfo: {
        ...purchaseRequest.purchaseRequestInfo,
        parts: parts,
        partCost: partsTotals.partCostTotal,
        estTax: partsTotals.estTaxTotal,
        restockingFee: partsTotals.restockFeeTotal,
        delivery: partsTotals.deliveryFeeTotal,
        installation: partsTotals.installationFeeTotal,
        total: partsTotals.partsTotal,
        savings: partsTotals.savings
      }
    };

    await dispatch(actions.change(FormNames.PurchasePartsForm, { ...prVM.purchaseRequestInfo }));

    await dispatch(savePurchaseRequestParts(prVM.id, prVM));
  };
}

/***** Save Purchase Request Parts *****/
export function savePurchaseRequestParts(id: number, pr: PurchaseRequestViewModel) {
  return async (dispatch: AppDispatcher, getState: () => AppState) => {
    dispatch(togglePartsLoading(true));
    let prInfo = pr.purchaseRequestInfo;
    let saveInfo = { ...prInfo };
    if (prInfo) {
      saveInfo.deliveryAddress = prInfo.deliveryAddress
        ? prInfo.deliveryAddress!.line1.length <= 0
          ? undefined
          : { ...prInfo.deliveryAddress }
        : undefined;

      saveInfo.parts = prInfo.parts.slice();
      if (prInfo.parts) {
        prInfo.parts.forEach((value, index) => {
          let savePart = { ...value };
          if (value.brand && value.brand.id <= 0) {
            savePart.brand = undefined;
          }
          saveInfo.parts.splice(index, 1, savePart);
        });
      }
    }
    let savePR = {
      ...pr,
      purchaseRequestInfo: saveInfo
    };
    await dispatch(
      requestAction(
        () => swoPurchasingService.savePurchaseRequestParts(id, savePR),
        () => {
          return { type: '' };
        },
        {
          hasToast: true,
          toastSuccessMessage: 'Parts Purchase Request Modified Successfully.'
        }
      )
    ).then(async () => {
      await dispatch(getPurchaseRequest(id)).then(async () => {
        await dispatch(togglePartsLoading(false));
      });
    });
  };
}



// WEBPACK FOOTER //
// ./src/purchasing/purchase-parts.actions.tsx